/**
 * PLEXTERRA Analytics Utility
 * Tracks page views, events, and conversions
 * 
 * Supports:
 * - Google Analytics 4 (GA4) - when MEASUREMENT_ID is configured
 * - Meta Pixel - when PIXEL_ID is configured
 * - LocalStorage logging - always enabled for debugging
 * - Console logging - for development
 */

// ============================================================
// EVENT CONSTANTS - Premium Simulator
// ============================================================

export const EVENTS = {
  // Premium Simulator Events
  PREMIUM_SIMULATOR_OPTIMIZE_CLICK: 'premium_simulator_optimize_click',
  PREMIUM_SIMULATOR_PREMIUM_CLICK: 'premium_simulator_premium_click',
  PREMIUM_SIMULATOR_AUTO_OPTIMIZE_CLICK: 'premium_simulator_auto_optimize_click',
  PREMIUM_SIMULATOR_AI_ANALYSIS_CLICK: 'premium_simulator_ai_analysis_click',
  
  // Land Analyzer Events
  LAND_ANALYZER_START: 'land_analyzer_start',
  LAND_ANALYZER_LEAD: 'land_analyzer_lead',
  
  // General Events
  CONTACT_FORM_SUBMIT: 'contact_form_submit',
  NEWSLETTER_SUBSCRIBE: 'newsletter_subscribe',
  PDF_DOWNLOAD: 'pdf_download',
  SERVICE_VIEW: 'service_view',
  MODEL_VIEW: 'model_view',
  QUOTE_REQUEST: 'quote_request',
};

// ============================================================
// LOCAL STORAGE TRACKING (always enabled)
// ============================================================

const STORAGE_KEY = 'plexterra_analytics_events';
const MAX_STORED_EVENTS = 100;

const storeEventLocally = (eventName, properties) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const events = stored ? JSON.parse(stored) : [];
    
    events.push({
      event: eventName,
      properties,
      timestamp: new Date().toISOString(),
      url: window.location.pathname,
    });
    
    // Keep only last N events
    const trimmedEvents = events.slice(-MAX_STORED_EVENTS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedEvents));
  } catch (e) {
    // Log so we can surface localStorage quota issues in dev; safe in prod (browsers ignore console.debug).
    console.debug('[analytics] localStorage write failed:', e.name);
  }
};

/**
 * Get all stored analytics events (for debugging)
 */
export const getAnalyticsHistory = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
};

/**
 * Clear stored analytics events
 */
export const clearAnalyticsHistory = () => {
  localStorage.removeItem(STORAGE_KEY);
};

// ============================================================
// GA4 INITIALIZATION
// ============================================================

// Initialize GA4
export const initGA = (measurementId) => {
  if (!measurementId || typeof window === 'undefined') return;
  
  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false // We'll send manually for SPA
  });
  
  console.log('GA4 initialized:', measurementId);
};

// Track page views (for SPA navigation)
export const trackPageView = (path, title) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href
  });
};

// Track custom events
export const trackEvent = (eventName, params = {}) => {
  // Enrich with common properties
  const enrichedParams = {
    ...params,
    language: document.documentElement.lang || 'fr',
    page_url: window.location.pathname,
    event_timestamp: new Date().toISOString(),
  };
  
  // Always log to console (gold color for Plexterra branding)
  console.log(
    `%c[Plexterra Analytics] ${eventName}`,
    'color: #C8A96A; font-weight: bold;',
    enrichedParams
  );
  
  // Always store locally for debugging
  storeEventLocally(eventName, enrichedParams);
  
  // Send to GA4 if available
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, enrichedParams);
  }
  
  // Send to Meta Pixel if available (map to standard events)
  if (typeof window !== 'undefined' && window.fbq) {
    const metaEventMap = {
      [EVENTS.PREMIUM_SIMULATOR_OPTIMIZE_CLICK]: 'Lead',
      [EVENTS.PREMIUM_SIMULATOR_PREMIUM_CLICK]: 'InitiateCheckout',
      [EVENTS.CONTACT_FORM_SUBMIT]: 'Contact',
      [EVENTS.NEWSLETTER_SUBSCRIBE]: 'Subscribe',
    };
    
    const metaEvent = metaEventMap[eventName];
    if (metaEvent) {
      window.fbq('track', metaEvent, { content_name: eventName, ...enrichedParams });
    } else {
      window.fbq('trackCustom', eventName, enrichedParams);
    }
  }
};

// ========== PREMIUM SIMULATOR EVENTS ==========

/**
 * Track Premium Simulator - "Optimiser mon projet" click
 */
export const trackPremiumSimulatorOptimize = (data) => {
  trackEvent(EVENTS.PREMIUM_SIMULATOR_OPTIMIZE_CLICK, {
    event_category: 'Premium Simulator',
    event_label: 'Optimize Project CTA',
    verdict: data.verdict,
    city: data.city,
    units: data.units,
    construction_type: data.construction_type,
    land_price: data.land_price,
    monthly_rent: data.monthly_rent,
    project_value: data.project_value,
    total_cost: data.total_cost,
    required_equity: data.required_equity,
  });
};

/**
 * Track Premium Simulator - "Choisir Premium" click
 */
export const trackPremiumSimulatorPremium = (data) => {
  trackEvent(EVENTS.PREMIUM_SIMULATOR_PREMIUM_CLICK, {
    event_category: 'Premium Simulator',
    event_label: 'Choose Premium CTA',
    verdict: data.verdict,
    city: data.city,
    units: data.units,
    construction_type: data.construction_type,
    land_price: data.land_price,
    monthly_rent: data.monthly_rent,
    project_value: data.project_value,
    total_cost: data.total_cost,
    required_equity: data.required_equity,
  });
};

/**
 * Track Premium Simulator - "Optimiser automatiquement" click
 */
export const trackPremiumSimulatorAutoOptimize = (data) => {
  trackEvent(EVENTS.PREMIUM_SIMULATOR_AUTO_OPTIMIZE_CLICK, {
    event_category: 'Premium Simulator',
    event_label: 'Auto Optimize Button',
    verdict: data.verdict,
    city: data.city,
    units: data.units,
    construction_type: data.construction_type,
    land_price: data.land_price,
    monthly_rent: data.monthly_rent,
    project_value: data.project_value,
    total_cost: data.total_cost,
    required_equity: data.required_equity,
  });
};

/**
 * Track Premium Simulator - "Générer l'analyse IA" click
 */
export const trackPremiumSimulatorAIAnalysis = (data) => {
  trackEvent(EVENTS.PREMIUM_SIMULATOR_AI_ANALYSIS_CLICK, {
    event_category: 'Premium Simulator',
    event_label: 'Generate AI Analysis Button',
    verdict: data.verdict,
    city: data.city,
    units: data.units,
    construction_type: data.construction_type,
    land_price: data.land_price,
    monthly_rent: data.monthly_rent,
    project_value: data.project_value,
    total_cost: data.total_cost,
    required_equity: data.required_equity,
  });
};

// ========== CONVERSION EVENTS ==========

// Land Analyzer - Lead captured
export const trackLandAnalyzerLead = (data) => {
  trackEvent('generate_lead', {
    event_category: 'Land Analyzer',
    event_label: data.recommended_model || 'Unknown',
    value: data.estimated_cost || 0,
    currency: 'CAD',
    lead_type: 'land_analyzer',
    model: data.recommended_model,
    city: data.city,
    estimated_roi: data.estimated_roi
  });
  
  // Also track as conversion
  trackEvent('conversion', {
    send_to: 'AW-CONVERSION_ID/CONVERSION_LABEL', // Replace with actual conversion ID
    value: 1,
    currency: 'CAD',
    transaction_id: data.lead_id
  });
};

// Quote Request submitted
export const trackQuoteRequest = (data) => {
  trackEvent('generate_lead', {
    event_category: 'Quote Request',
    event_label: data.model || 'General',
    value: data.estimated_value || 0,
    currency: 'CAD',
    lead_type: 'quote_request'
  });
};

// Contact form submitted
export const trackContactSubmit = (data) => {
  trackEvent('generate_lead', {
    event_category: 'Contact Form',
    event_label: data.subject || 'General Inquiry',
    lead_type: 'contact'
  });
};

// Newsletter signup
export const trackNewsletterSignup = (data) => {
  trackEvent('sign_up', {
    event_category: 'Newsletter',
    event_label: data.source || 'Unknown',
    method: 'email'
  });
};

// Model page view (for SEO tracking)
export const trackModelView = (modelId, modelName) => {
  trackEvent('view_item', {
    event_category: 'Model',
    event_label: modelName,
    items: [{
      item_id: modelId,
      item_name: modelName,
      item_category: 'Multiplex Plans'
    }]
  });
};

// Land Analyzer started (funnel tracking)
export const trackLandAnalyzerStart = () => {
  trackEvent('begin_checkout', {
    event_category: 'Land Analyzer',
    event_label: 'Analysis Started'
  });
};

// CTA Click tracking
export const trackCTAClick = (ctaName, destination) => {
  trackEvent('select_content', {
    event_category: 'CTA',
    event_label: ctaName,
    content_type: 'button',
    destination: destination
  });
};

// Search/Filter usage
export const trackSearch = (searchTerm, category) => {
  trackEvent('search', {
    search_term: searchTerm,
    event_category: category || 'Site Search'
  });
};

// Scroll depth tracking
export const trackScrollDepth = (depth) => {
  trackEvent('scroll', {
    event_category: 'Engagement',
    event_label: `${depth}%`,
    value: depth
  });
};

// Time on page tracking
export const trackTimeOnPage = (seconds, pagePath) => {
  trackEvent('timing_complete', {
    event_category: 'Engagement',
    event_label: pagePath,
    value: seconds,
    name: 'time_on_page'
  });
};

// Export all functions
export default {
  // Initialization
  initGA,
  
  // Generic tracking
  trackPageView,
  trackEvent,
  
  // Premium Simulator (NEW)
  trackPremiumSimulatorOptimize,
  trackPremiumSimulatorPremium,
  trackPremiumSimulatorAutoOptimize,
  trackPremiumSimulatorAIAnalysis,
  
  // Land Analyzer
  trackLandAnalyzerLead,
  trackLandAnalyzerStart,
  
  // Forms & Leads
  trackQuoteRequest,
  trackContactSubmit,
  trackNewsletterSignup,
  
  // Content
  trackModelView,
  trackCTAClick,
  trackSearch,
  
  // Engagement
  trackScrollDepth,
  trackTimeOnPage,
  
  // Debug utilities
  getAnalyticsHistory,
  clearAnalyticsHistory,
  
  // Event constants
  EVENTS,
};
